// src/pages/ClientManagement.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, doc, deleteDoc, updateDoc, query, orderBy } from 'firebase/firestore';
import { useUserRole } from '../hooks/useUserRole';
import Navigation from '../components/Navigation';

interface Client {
  id: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  businessName: string;
  businessType: string;
  ein?: string;
  selectedServices?: string[];
  comments?: string;
  uploadedFiles?: string[];
  userId?: string;
  createdAt?: any;
  updatedAt?: any;
}

const ClientManagement: React.FC = () => {
  const { role, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editBusinessName, setEditBusinessName] = useState('');
  const [editBusinessType, setEditBusinessType] = useState('');
  const [editEin, setEditEin] = useState('');

  useEffect(() => {
    if (!roleLoading && role !== 'admin' && role !== 'staff') {
      navigate('/client-dashboard');
    }
  }, [roleLoading, role, navigate]);

  useEffect(() => {
    if (role === 'admin' || role === 'staff') {
      loadClients();
    }
  }, [role]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const clientsRef = collection(db, 'clients');
      const clientsQuery = query(clientsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(clientsQuery);
      
      const clientsData: Client[] = [];
      snapshot.forEach((doc) => {
        clientsData.push({
          id: doc.id,
          ...doc.data()
        } as Client);
      });
      
      setClients(clientsData);
    } catch (error: any) {
      console.error('Error loading clients:', error);
      setError('Failed to load clients: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async (clientId: string, clientName: string) => {
    if (!window.confirm(`Are you sure you want to delete client "${clientName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      const clientRef = doc(db, 'clients', clientId);
      await deleteDoc(clientRef);

      setSuccess('Client deleted successfully!');
      await loadClients();
      setSelectedClient(null);
    } catch (error: any) {
      console.error('Error deleting client:', error);
      setError('Failed to delete client: ' + error.message);
    }
  };

  const handleEditClient = () => {
    if (selectedClient) {
      // Split fullName into firstName and lastName if they exist, otherwise use empty strings
      if (selectedClient.firstName && selectedClient.lastName) {
        setEditFirstName(selectedClient.firstName);
        setEditLastName(selectedClient.lastName);
      } else if (selectedClient.fullName) {
        const nameParts = selectedClient.fullName.trim().split(/\s+/);
        setEditFirstName(nameParts[0] || '');
        setEditLastName(nameParts.slice(1).join(' ') || '');
      } else {
        setEditFirstName('');
        setEditLastName('');
      }
      setEditEmail(selectedClient.email || '');
      setEditBusinessName(selectedClient.businessName || '');
      setEditBusinessType(selectedClient.businessType || '');
      setEditEin(selectedClient.ein || '');
      setIsEditingClient(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingClient(false);
    setEditFirstName('');
    setEditLastName('');
    setEditEmail('');
    setEditBusinessName('');
    setEditBusinessType('');
    setEditEin('');
  };

  const handleSaveClient = async () => {
    if (!selectedClient) return;

    try {
      setError('');
      setSuccess('');
      
      const fullName = `${editFirstName.trim()} ${editLastName.trim()}`.trim();
      const clientRef = doc(db, 'clients', selectedClient.id);
      
      await updateDoc(clientRef, {
        firstName: editFirstName.trim(),
        lastName: editLastName.trim(),
        fullName: fullName,
        email: editEmail.trim(),
        businessName: editBusinessName.trim(),
        businessType: editBusinessType.trim(),
        ein: editEin.trim(),
        updatedAt: new Date()
      });

      setSuccess('Client information updated successfully!');
      setIsEditingClient(false);
      await loadClients();
      
      // Update selected client to reflect changes
      const updatedClients = await getDocs(query(collection(db, 'clients'), orderBy('createdAt', 'desc')));
      const updatedClientData = updatedClients.docs.find(doc => doc.id === selectedClient.id);
      if (updatedClientData) {
        setSelectedClient({
          id: updatedClientData.id,
          ...updatedClientData.data()
        } as Client);
      }
    } catch (error: any) {
      console.error('Error updating client:', error);
      setError('Failed to update client information: ' + error.message);
    }
  };

  const filteredClients = clients.filter((client) => {
    const searchLower = searchTerm.toLowerCase();
    const fullName = client.fullName || (client.firstName && client.lastName ? `${client.firstName} ${client.lastName}` : '');
    return (
      (fullName && fullName.toLowerCase().includes(searchLower)) ||
      client.email.toLowerCase().includes(searchLower) ||
      client.businessName.toLowerCase().includes(searchLower) ||
      (client.businessType && client.businessType.toLowerCase().includes(searchLower))
    );
  });

  if (roleLoading || loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (role !== 'admin' && role !== 'staff') {
    return null;
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1>Client Management</h1>
          <button
            onClick={() => navigate(role === 'admin' ? '/admin-dashboard' : '/staff-dashboard')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Statistics */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <StatCard title="Total Clients" value={clients.length} color="#007bff" />
          <StatCard 
            title="With Documents" 
            value={clients.filter(c => c.uploadedFiles && c.uploadedFiles.length > 0).length} 
            color="#28a745" 
          />
        </div>

        {/* Alerts */}
        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{
            backgroundColor: '#d4edda',
            color: '#155724',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #c3e6cb'
          }}>
            {success}
          </div>
        )}

        {/* Search */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Search Clients
          </label>
          <input
            type="text"
            placeholder="Search by name, email, or business..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '1rem'
            }}
          />
        </div>

        {/* Clients List and Details */}
        <div style={{ display: 'grid', gridTemplateColumns: selectedClient ? '1fr 1fr' : '1fr', gap: '2rem' }}>
          {/* Clients Table */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Client</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Business</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Services</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                      {loading ? 'Loading clients...' : 'No clients found'}
                    </td>
                  </tr>
                ) : (
                  filteredClients.map((client) => (
                    <tr 
                      key={client.id} 
                      style={{ 
                        borderBottom: '1px solid #dee2e6',
                        backgroundColor: selectedClient?.id === client.id ? '#e7f3ff' : 'white',
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedClient(client)}
                    >
                      <td style={{ padding: '1rem' }}>
                        <div>
                          <div style={{ fontWeight: '500' }}>
                            {client.fullName || (client.firstName && client.lastName ? `${client.firstName} ${client.lastName}` : 'N/A')}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>{client.email}</div>
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div>
                          <div style={{ fontWeight: '500' }}>{client.businessName}</div>
                          <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>{client.businessType}</div>
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {client.selectedServices && client.selectedServices.length > 0 ? (
                          <div style={{ fontSize: '0.875rem' }}>
                            {client.selectedServices.slice(0, 2).join(', ')}
                            {client.selectedServices.length > 2 && ` +${client.selectedServices.length - 2}`}
                          </div>
                        ) : (
                          <span style={{ color: '#6c757d', fontSize: '0.875rem' }}>None</span>
                        )}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        {role === 'admin' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const clientName = client.fullName || (client.firstName && client.lastName ? `${client.firstName} ${client.lastName}` : client.email);
                              handleDeleteClient(client.id, clientName);
                            }}
                            style={{
                              padding: '0.25rem 0.75rem',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.875rem'
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Client Details Panel */}
          {selectedClient && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              padding: '2rem',
              position: 'sticky',
              top: '2rem',
              maxHeight: 'calc(100vh - 4rem)',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0 }}>Client Details</h2>
                <button
                  onClick={() => {
                    setSelectedClient(null);
                    setIsEditingClient(false);
                    handleCancelEdit();
                  }}
                  style={{
                    padding: '0.5rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0, color: '#495057' }}>Contact Information</h3>
                  {!isEditingClient && (
                    <button
                      onClick={handleEditClient}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      Edit Client
                    </button>
                  )}
                </div>
                
                {isEditingClient ? (
                  <div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
                        First Name
                      </label>
                      <input
                        type="text"
                        value={editFirstName}
                        onChange={(e) => setEditFirstName(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={editLastName}
                        onChange={(e) => setEditLastName(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
                        Email
                      </label>
                      <input
                        type="email"
                        value={editEmail}
                        onChange={(e) => setEditEmail(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <DetailRow label="Full Name" value={selectedClient.fullName || (selectedClient.firstName && selectedClient.lastName ? `${selectedClient.firstName} ${selectedClient.lastName}` : 'N/A')} />
                    {selectedClient.firstName && selectedClient.lastName && (
                      <>
                        <DetailRow label="First Name" value={selectedClient.firstName} />
                        <DetailRow label="Last Name" value={selectedClient.lastName} />
                      </>
                    )}
                    <DetailRow label="Email" value={selectedClient.email} />
                    {selectedClient.userId && (
                      <DetailRow label="User ID" value={selectedClient.userId} />
                    )}
                  </>
                )}
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#495057' }}>Business Information</h3>
                {isEditingClient ? (
                  <div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
                        Business Name
                      </label>
                      <input
                        type="text"
                        value={editBusinessName}
                        onChange={(e) => setEditBusinessName(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
                        Business Type
                      </label>
                      <input
                        type="text"
                        value={editBusinessType}
                        onChange={(e) => setEditBusinessType(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
                        EIN
                      </label>
                      <input
                        type="text"
                        value={editEin}
                        onChange={(e) => setEditEin(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                      <button
                        onClick={handleSaveClient}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem'
                        }}
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <DetailRow label="Business Name" value={selectedClient.businessName} />
                    <DetailRow label="Business Type" value={selectedClient.businessType} />
                    {selectedClient.ein && (
                      <DetailRow label="EIN" value={selectedClient.ein} />
                    )}
                  </>
                )}
              </div>

              {selectedClient.selectedServices && selectedClient.selectedServices.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#495057' }}>Selected Services</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {selectedClient.selectedServices.map((service, index) => (
                      <span
                        key={index}
                        style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: '#e7f3ff',
                          color: '#0066cc',
                          borderRadius: '12px',
                          fontSize: '0.875rem'
                        }}
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedClient.comments && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#495057' }}>Comments</h3>
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word'
                  }}>
                    {selectedClient.comments}
                  </div>
                </div>
              )}

              {selectedClient.uploadedFiles && selectedClient.uploadedFiles.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#495057' }}>Uploaded Documents</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {selectedClient.uploadedFiles.map((fileUrl, index) => (
                      <a
                        key={index}
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '0.5rem',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '4px',
                          textDecoration: 'none',
                          color: '#007bff',
                          fontSize: '0.875rem',
                          wordBreak: 'break-all'
                        }}
                      >
                        📄 Document {index + 1}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {selectedClient.createdAt && (
                <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #dee2e6' }}>
                  <DetailRow 
                    label="Created" 
                    value={selectedClient.createdAt?.toDate ? selectedClient.createdAt.toDate().toLocaleString() : 'N/A'} 
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface StatCardProps {
  title: string;
  value: number;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, color }) => (
  <div style={{
    backgroundColor: 'white',
    padding: '1rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderLeft: `4px solid ${color}`
  }}>
    <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.5rem' }}>
      {title}
    </div>
    <div style={{ fontSize: '2rem', fontWeight: 'bold', color }}>
      {value}
    </div>
  </div>
);

interface DetailRowProps {
  label: string;
  value: string | undefined;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <div style={{ marginBottom: '0.75rem' }}>
    <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
      {label}
    </div>
    <div style={{ fontSize: '1rem', fontWeight: '500' }}>
      {value || 'N/A'}
    </div>
  </div>
);

export default ClientManagement;

